import React, { useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkReference from '@/lib/remark-reference';
import remarkChart from '@/lib/remark-chart';
import rehypeRaw from 'rehype-raw';
import rehypeReference from '@/lib/rehype-reference';
import rehypeChart from '@/lib/rehype-chart';
import rehypeAsset from '@/lib/rehype-asset';
import { preprocessAssetTags } from '@/lib/asset-tag-parser';
import { ChevronUp, ChevronDown, Lock } from 'lucide-react';
import { useLanguage } from '@/contexts/language-context';
import {
  segmentContent,
  hasIncompleteTable,
  parseMarkdownTableSafe,
  looksLikeTable,
  extractCodeBlockContent,
  sortTableData,
  type TableData,
  type SortState,
} from '@/lib/markdown-utils';
import { stripFollowupTags, parseFollowupQueries } from '@/lib/followup-parser';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Reference } from '@/components/reference';
// Upgraded to TradingView Advanced Charts
import { TradingViewChart as Chart } from '@/components/tradingview-chart';
import { AssetChart } from '@/components/asset-chart';
import TradeRecommendation from '@/components/trade-recommendation';
import { Button } from '@/components/ui/button';

interface MessageContentProps {
  content: string;
  isStreaming?: boolean;
  role: 'user' | 'assistant';
  hasBeenParsed?: boolean;
  sessionId?: string;
  userId?: string;
  klineChartEnabled?: boolean;
  klineRestriction?: 'login' | 'upgrade';
  onLockedKlineClick?: () => void;
  tradeRecommendations?: Array<{
    asset: string;
    deeplink: string;
    price?: string;
  }>;
}

/**
 * Support/Resistance level type
 */
export interface SRLevel {
  price: number;
  touches: number;
  strength: number;
}

export interface SRLevels {
  resistance: SRLevel[];
  support: SRLevel[];
}

// Shared markdown plugin configurations for prose rendering paths.
// Static arrays at module level ensure stable identity across renders.
const PROSE_REMARK_PLUGINS = [remarkGfm, remarkReference, remarkChart];
const PROSE_REHYPE_PLUGINS = [rehypeRaw, rehypeReference, rehypeChart, rehypeAsset];

const LOCKED_KLINE_BARS = [
  { h: 48, y: 34, gain: true },
  { h: 66, y: 24, gain: false },
  { h: 40, y: 40, gain: true },
  { h: 74, y: 18, gain: true },
  { h: 58, y: 30, gain: false },
  { h: 86, y: 10, gain: true },
  { h: 52, y: 38, gain: false },
  { h: 70, y: 22, gain: true },
  { h: 44, y: 44, gain: false },
  { h: 78, y: 14, gain: true },
  { h: 60, y: 28, gain: true },
  { h: 50, y: 36, gain: false },
];

function getLockedKlineCopy(language?: string, restriction: 'login' | 'upgrade' = 'login') {
  if (restriction === 'upgrade') {
    return language === 'zh'
      ? {
          title: '升级以解锁实时 K 线',
          description: '您当前的套餐暂不包含 K 线图权限。',
          cta: '升级',
        }
      : {
          title: 'Upgrade to unlock live K-line charts',
          description: 'K-line chart access is not included in your current plan.',
          cta: 'Upgrade',
        };
  }

  if (language === 'zh') {
    return {
      title: '注册后解锁实时K线',
      description: '查看实时K线与深度技术分析，请先免费注册',
      cta: '免费注册',
    };
  }

  return {
    title: 'Sign up to unlock live K-line charts',
    description: 'Create a free account to view real-time K-line charts and deeper technical analysis.',
    cta: 'Sign up free',
  };
}

function LockedKlinePlaceholder({ language, restriction, onUnlockClick }: { language?: string; restriction?: 'login' | 'upgrade'; onUnlockClick?: () => void }) {
  const copy = getLockedKlineCopy(language, restriction);

  return (
    <div
      data-testid="locked-kline-placeholder"
      className="relative my-4 min-h-[280px] overflow-hidden rounded-lg border border-white/16 bg-[#141210] shadow-sm sm:min-h-[340px]">
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-55 blur-[2px]"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-x-6 bottom-8 top-8 flex items-end justify-between gap-2 opacity-80 sm:inset-x-10">
        {LOCKED_KLINE_BARS.map((bar, index) => (
          <div
            key={index}
            className="relative flex h-full flex-1 items-end justify-center">
            <span
              className={bar.gain ? 'absolute w-px bg-[#16C784]/70' : 'absolute w-px bg-[#EA3943]/70'}
              style={{ height: `${bar.h}%`, bottom: `${Math.max(0, bar.y - 10)}%` }}
            />
            <span
              className={bar.gain ? 'w-3 rounded-sm bg-[#16C784]/85 sm:w-4' : 'w-3 rounded-sm bg-[#EA3943]/85 sm:w-4'}
              style={{ height: `${Math.max(18, Math.round(bar.h * 0.42))}%`, marginBottom: `${bar.y}%` }}
            />
          </div>
        ))}
      </div>
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-[#070400]/65"
      />
      <div className="relative z-10 flex min-h-[280px] flex-col items-center justify-center px-5 py-8 text-center sm:min-h-[340px]">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-white/16 bg-white/8 text-[#F98715]">
          <Lock className="h-5 w-5" />
        </div>
        <div className="max-w-md">
          <h3 className="text-lg font-semibold text-white sm:text-xl">{copy.title}</h3>
          <p className="mt-2 text-sm leading-6 text-[#ccc] sm:text-base">{copy.description}</p>
        </div>
        <Button
          type="button"
          onClick={onUnlockClick}
          className="mt-5 h-10 bg-[#F98715] px-5 text-white hover:bg-[#F98715] hover:text-white">
          {copy.cta}
        </Button>
      </div>
    </div>
  );
}

/**
 * Preprocesses content to replace <chart> tags with placeholder spans
 * This prevents the raw tag from flashing on screen before plugins process it
 */
function preprocessChartTags(content: string): string {
  // Extract chart tags with support_resistance
  const CHART_TAG_REGEX = /<chart\s+[^>]*\/>/g;

  const result = content.replace(CHART_TAG_REGEX, (match) => {
    // Extract all attributes using individual patterns
    const typeMatch = match.match(/type="(candlestick|line)"/);
    const dataIdMatch = match.match(/data_id="([^"]+)"/);
    const studiesMatch = match.match(/studies="([^"]*)"/);
    const tickerMatch = match.match(/ticker="([^"]*)"/);
    const supportResistanceMatch = match.match(/support_resistance='([^']+)'/);
    const startDateMatch = match.match(/start_date="([^"]+)"/);
    const endDateMatch = match.match(/end_date="([^"]+)"/);
    const comparisonMatch = match.match(/comparison="(price|indicator)"/);
    const tickersMatch = match.match(/tickers="([^"]*)"/);
    const dataIdsMatch = match.match(/data_ids="([^"]*)"/);
    const indicatorMatch = match.match(/indicator="([^"]*)"/);

    const type = typeMatch ? typeMatch[1] : 'candlestick';
    const dataId = dataIdMatch ? dataIdMatch[1] : '';
    const studies = studiesMatch ? studiesMatch[1] : '';
    const ticker = tickerMatch ? tickerMatch[1] : '';
    const startDate = startDateMatch ? startDateMatch[1] : '';
    const endDate = endDateMatch ? endDateMatch[1] : '';
    const comparison = comparisonMatch ? comparisonMatch[1] : '';
    const tickers = tickersMatch ? tickersMatch[1] : '';
    const dataIds = dataIdsMatch ? dataIdsMatch[1] : '';
    const indicator = indicatorMatch ? indicatorMatch[1] : '';

    // Parse support_resistance JSON if present
    let srData = null;
    if (supportResistanceMatch) {
      try {
        srData = JSON.parse(supportResistanceMatch[1]);
        console.log('[preprocessChartTags] Parsed S/R data from chart tag:', srData);
      } catch (e) {
        console.error('[preprocessChartTags] Failed to parse S/R data:', e);
      }
    }

    // Replace with span that our component handler recognizes
    let spanAttrs = `data-chart="true" data-chart-type="${type}" data-chart-id="${dataId}"`;
    if (studies) spanAttrs += ` data-chart-studies="${studies}"`;
    if (ticker) spanAttrs += ` data-chart-ticker="${ticker}"`;
    if (startDate) spanAttrs += ` data-chart-start-date="${startDate}"`;
    if (endDate) spanAttrs += ` data-chart-end-date="${endDate}"`;
    if (comparison) spanAttrs += ` data-chart-comparison="${comparison}"`;
    if (tickers) spanAttrs += ` data-chart-tickers="${tickers}"`;
    if (dataIds) spanAttrs += ` data-chart-data-ids="${dataIds}"`;
    if (indicator) spanAttrs += ` data-chart-indicator="${indicator}"`;
    if (srData) {
      // Store as JSON string in data attribute
      const srJson = JSON.stringify(srData).replace(/"/g, '&quot;');
      spanAttrs += ` data-chart-sr="${srJson}"`;
    }

    return `<span ${spanAttrs}></span>`;
  });

  return result;
}

// Memoized components for table cell markdown rendering
const tableCellComponents = {
  // Only allow inline elements, no block elements
  p: ({ children }: { children?: React.ReactNode }) => <span>{children}</span>, // Convert paragraphs to spans
  h1: ({ children }: { children?: React.ReactNode }) => <strong>{children}</strong>, // Convert headings to strong
  h2: ({ children }: { children?: React.ReactNode }) => <strong>{children}</strong>,
  h3: ({ children }: { children?: React.ReactNode }) => <strong>{children}</strong>,
  h4: ({ children }: { children?: React.ReactNode }) => <strong>{children}</strong>,
  h5: ({ children }: { children?: React.ReactNode }) => <strong>{children}</strong>,
  h6: ({ children }: { children?: React.ReactNode }) => <strong>{children}</strong>,
  ul: ({ children }: { children?: React.ReactNode }) => <span>{children}</span>, // Flatten lists to inline
  ol: ({ children }: { children?: React.ReactNode }) => <span>{children}</span>,
  li: ({ children }: { children?: React.ReactNode }) => <span>{children} </span>,
  blockquote: ({ children }: { children?: React.ReactNode }) => <span>{children}</span>,
  code: (props: React.ComponentProps<'code'>) => (
    <code
      className="bg-[#F98715]/15 text-[#F98715] px-1 py-0.5 rounded text-sm font-mono"
      {...props}>
      {props.children}
    </code>
  ),
  // Allow these inline elements as-is
  strong: ({ children }: { children?: React.ReactNode }) => <strong>{children}</strong>,
  em: ({ children }: { children?: React.ReactNode }) => <em>{children}</em>,
  a: ({ children, href }: { children?: React.ReactNode; href?: string }) => (
    <a
      href={href}
      className="text-blue-600 hover:underline"
      target="_blank"
      rel="noopener noreferrer">
      {children}
    </a>
  ),
  del: ({ children }: { children?: React.ReactNode }) => <del className="text-white">{children}</del>,
  // Handle inline references (charts not supported in table cells)
  span: (props: React.ComponentProps<'span'>) => {
    const dataProps = props as Record<string, unknown>;
    if ('dataReference' in dataProps || 'data-reference' in dataProps) {
      const name = (dataProps['dataName'] || dataProps['data-name']) as string;
      const url = (dataProps['dataUrl'] || dataProps['data-url']) as string | undefined;
      return <Reference data={{ name, url }} />;
    }
    return <span {...props}>{props.children}</span>;
  },
  // Remove line breaks and other block elements
  br: () => <span> </span>,
};

// Component to render table cell content with inline markdown support
const TableCellContent = ({ content }: { content: string }) => {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkReference, remarkChart]}
      rehypePlugins={[rehypeRaw, rehypeReference, rehypeChart]}
      components={tableCellComponents}>
      {content}
    </ReactMarkdown>
  );
};

// Component to render table data using shadcn/ui Table components
const TableRenderer = ({ tableData }: { tableData: TableData }) => {
  const [sortState, setSortState] = useState<SortState>({ column: null, direction: null });
  const [originalData] = useState<TableData>(tableData); // Store original unsorted data

  const getAlignmentClass = (alignment: 'left' | 'center' | 'right') => {
    switch (alignment) {
      case 'center':
        return 'text-center';
      case 'right':
        return 'text-right';
      default:
        return 'text-left';
    }
  };

  const handleHeaderClick = (columnIndex: number) => {
    setSortState((prevState) => {
      if (prevState.column === columnIndex) {
        // Same column clicked - cycle through states
        if (prevState.direction === null) {
          return { column: columnIndex, direction: 'asc' };
        } else if (prevState.direction === 'asc') {
          return { column: columnIndex, direction: 'desc' };
        } else {
          return { column: null, direction: null }; // Reset to unsorted
        }
      } else {
        // Different column clicked - start with ascending
        return { column: columnIndex, direction: 'asc' };
      }
    });
  };

  const getSortIcon = (columnIndex: number) => {
    if (sortState.column !== columnIndex) return null;

    if (sortState.direction === 'asc') {
      return <ChevronUp className="w-4 h-4" />;
    } else if (sortState.direction === 'desc') {
      return <ChevronDown className="w-4 h-4" />;
    }
    return null;
  };

  // Get the data to display (sorted or original)
  const displayData = sortState.column !== null && sortState.direction !== null ? sortTableData(originalData, sortState.column, sortState.direction) : originalData;

  return (
    <div className="my-3 w-full max-w-full sm:my-6">
      <div className="overflow-x-auto rounded-md border border-white/16 shadow-sm scrollbar-track-[#2c2c2c] scrollbar-thumb-[#9f9f9f] sm:rounded-lg">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#231F20] hover:bg-[#231F20] border-b-white/16">
              {displayData.headers.map((header, index) => (
                <TableHead
                  key={index}
                  className={`font-semibold py-2 sm:py-3 px-2 sm:px-4 cursor-pointer  text-white transition-colors select-none text-xs sm:text-sm ${
                    displayData.alignment ? getAlignmentClass(displayData.alignment[index]) : 'text-left'
                  } ${sortState.column === index ? 'bg-muted/60' : ''}`}
                  onClick={() => handleHeaderClick(index)}
                  title={`Click to sort by ${header}`}>
                  <div className="flex items-center gap-1 sm:gap-2">
                    <TableCellContent content={header} />
                    {getSortIcon(index)}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayData.rows.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                className="border-b-white/16 hover:bg-white/6 transition-colors">
                {row.map((cell, cellIndex) => (
                  <TableCell
                    key={cellIndex}
                    className={`py-2 sm:py-3 px-2 sm:px-4 text-[#ccc] text-xs sm:text-sm ${
                      displayData.alignment ? getAlignmentClass(displayData.alignment[cellIndex]) : 'text-left'
                    }`}>
                    <TableCellContent content={cell} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default function MessageContent({ content, isStreaming = false, role, hasBeenParsed = false, sessionId, userId, klineChartEnabled = true, klineRestriction, onLockedKlineClick, tradeRecommendations }: MessageContentProps) {
  const { t, language } = useLanguage();
  const isKlineLocked = role === 'assistant' && !klineChartEnabled;

  // Preprocess content to replace chart/asset tags before markdown rendering.
  // Chart/asset preprocessing only applies to the ReactMarkdown path — plain-text
  // paths (user messages, streaming raw fallback) must not show span markup.
  const processedContent = useMemo(() => {
    let processed = preprocessChartTags(content);
    processed = preprocessAssetTags(processed);

    // Strip followup tags to prevent raw XML from showing in ReactMarkdown
    // During streaming: strip tags completely
    // After streaming: parse and remove tags (they'll be rendered separately as buttons)
    if (isStreaming) {
      processed = stripFollowupTags(processed);
    } else if (role === 'assistant') {
      const { cleanedContent } = parseFollowupQueries(processed);
      processed = cleanedContent;
    }

    return processed;
  }, [content, isStreaming, role]);

  // Plain-text content for raw rendering: strip followups but no chart/asset preprocessing
  const plainTextContent = useMemo(() => {
    return isStreaming ? stripFollowupTags(content) : content;
  }, [content, isStreaming]);

  // Segment the content into text and table parts
  // useMemo ensures recalculation only when content changes
  const segments = useMemo(() => segmentContent(processedContent), [processedContent]);

  // Memoize ReactMarkdown components to prevent Chart remounting
  const markdownComponents = useMemo(
    () => ({
      // Ensure paragraphs don't have extra margin
      p: ({ children }: { children?: React.ReactNode }) => <div className="mb-2">{children}</div>,
      // Style headers with distinct sizes and spacing
      h1: ({ children }: { children?: React.ReactNode }) => <h1 className="text-2xl font-bold mt-6 mb-3 text-white">{children}</h1>,
      h2: ({ children }: { children?: React.ReactNode }) => <h2 className="text-xl font-bold mt-5 mb-2 text-white">{children}</h2>,
      h3: ({ children }: { children?: React.ReactNode }) => <h3 className="text-lg font-semibold mt-4 mb-2 text-white">{children}</h3>,
      h4: ({ children }: { children?: React.ReactNode }) => <h4 className="text-base font-semibold mt-3 mb-1.5 text-white">{children}</h4>,
      h5: ({ children }: { children?: React.ReactNode }) => <h5 className="text-sm font-semibold mt-2 mb-1 text-white">{children}</h5>,
      h6: ({ children }: { children?: React.ReactNode }) => <h6 className="text-sm font-medium mt-2 mb-1 text-white">{children}</h6>,
      // Style lists with proper spacing and indentation
      ul: ({ children }: { children?: React.ReactNode }) => <ul className="list-disc list-inside my-2 space-y-1 ml-2">{children}</ul>,
      ol: ({ children }: { children?: React.ReactNode }) => <ol className="list-decimal list-inside my-2 space-y-1 ml-2">{children}</ol>,
      li: ({ children }: { children?: React.ReactNode }) => <li className="text-white leading-relaxed">{children}</li>,
      // Style blockquotes
      blockquote: ({ children }: { children?: React.ReactNode }) => (
        <blockquote className="border-l-4 border-[#F98715] pl-4 py-2 my-3 italic text-[#ccc] bg-[#231F20]">{children}</blockquote>
      ),
      // Style links
      a: ({ children, href }: { children?: React.ReactNode; href?: string }) => (
        <a
          href={href}
          className="text-[#F98715] hover:text-[#F98715] underline"
          target="_blank"
          rel="noopener noreferrer">
          {children}
        </a>
      ),
      // Style horizontal rules
      hr: () => <hr className="my-4 border-t border-white/16" />,
      // Style strong and emphasis
      strong: ({ children }: { children?: React.ReactNode }) => <strong className="font-semibold text-white">{children}</strong>,
      em: ({ children }: { children?: React.ReactNode }) => <em className="italic text-white">{children}</em>,
      // Style code blocks
      code: (props: React.ComponentProps<'code'>) => (
        <code
          className="bg-[#F98715]/15 text-[#F98715] px-1 py-0.5 rounded text-sm font-mono "
          {...props}>
          {props.children}
        </code>
      ),
      // Handle inline references, charts, and enrichment placeholders
      span: (props: React.ComponentProps<'span'>) => {
        const dataProps = props as Record<string, unknown>;

        // Handle references
        if ('dataReference' in dataProps || 'data-reference' in dataProps) {
          const name = (dataProps['dataName'] || dataProps['data-name']) as string;
          const url = (dataProps['dataUrl'] || dataProps['data-url']) as string | undefined;
          return <Reference data={{ name, url }} />;
        }

        // Handle charts
        if ('dataChart' in dataProps || 'data-chart' in dataProps) {
          if (isKlineLocked) {
            return (
              <LockedKlinePlaceholder
                language={language}
                restriction={klineRestriction}
                onUnlockClick={onLockedKlineClick}
              />
            );
          }

          const chartType = (dataProps['dataChartType'] || dataProps['data-chart-type']) as 'candlestick' | 'line';
          const dataId = (dataProps['dataChartId'] || dataProps['data-chart-id']) as string;
          const ticker = (dataProps['dataChartTicker'] || dataProps['data-chart-ticker']) as string | undefined;
          const studies = (dataProps['dataChartStudies'] || dataProps['data-chart-studies']) as string | undefined;
          const startDate = (dataProps['dataChartStartDate'] || dataProps['data-chart-start-date']) as string | undefined;
          const endDate = (dataProps['dataChartEndDate'] || dataProps['data-chart-end-date']) as string | undefined;
          const comparison = (dataProps['dataChartComparison'] || dataProps['data-chart-comparison']) as 'price' | 'indicator' | undefined;
          const tickersString = (dataProps['dataChartTickers'] || dataProps['data-chart-tickers']) as string | undefined;
          const dataIdsString = (dataProps['dataChartDataIds'] || dataProps['data-chart-data-ids']) as string | undefined;
          const indicator = (dataProps['dataChartIndicator'] || dataProps['data-chart-indicator']) as string | undefined;
          const srDataString = (dataProps['dataChartSr'] || dataProps['data-chart-sr']) as string | undefined;

          // Parse tickers array from comma-separated string (for display/legend)
          const tickers = tickersString ? tickersString.split(',').map((t) => t.trim()) : undefined;

          // Parse data IDs array from comma-separated string (for fetching individual datapacks)
          const dataIds = dataIdsString ? dataIdsString.split(',').map((id) => id.trim()) : undefined;

          // Parse S/R data from data attribute
          let srData: SRLevels | null = null;
          if (srDataString) {
            try {
              srData = JSON.parse(srDataString);
              console.log('[MessageContent] Parsed S/R data for chart:', srData);
            } catch (e) {
              console.error('[MessageContent] Failed to parse S/R data:', e);
            }
          }

          return (
            <Chart
              key={dataId}
              type={chartType}
              dataId={dataId}
              ticker={ticker}
              studies={studies}
              sessionId={sessionId}
              userId={userId}
              supportResistance={srData}
              startDate={startDate}
              endDate={endDate}
              comparison={comparison}
              tickers={tickers}
              dataIds={dataIds}
              indicator={indicator}
            />
          );
        }

        // Handle asset charts
        if ('dataAsset' in dataProps || 'data-asset' in dataProps) {
          if (isKlineLocked) {
            return (
              <LockedKlinePlaceholder
                language={language}
                restriction={klineRestriction}
                onUnlockClick={onLockedKlineClick}
              />
            );
          }

          const symbol = (dataProps['dataAssetSymbol'] || dataProps['data-asset-symbol']) as string;
          const assetType = (dataProps['dataAssetType'] || dataProps['data-asset-type']) as string;
          const interval = (dataProps['dataAssetInterval'] || dataProps['data-asset-interval']) as string | undefined;
          const chartType = (dataProps['dataAssetChartType'] || dataProps['data-asset-chart-type']) as string | undefined;
          const start = (dataProps['dataAssetStart'] || dataProps['data-asset-start']) as string | undefined;
          const end = (dataProps['dataAssetEnd'] || dataProps['data-asset-end']) as string | undefined;
          const studies = (dataProps['dataAssetStudies'] || dataProps['data-asset-studies']) as string | undefined;

          return (
            <AssetChart
              symbol={symbol}
              assetType={assetType}
              interval={interval}
              chartType={chartType as 'candlestick' | 'line' | undefined}
              start={start}
              end={end}
              studies={studies}
            />
          );
        }

        return <span {...props}>{props.children}</span>;
      },
    }),
    [isKlineLocked, klineRestriction, language, onLockedKlineClick, sessionId, userId],
  );

  // User messages always render as plain text — use original content
  // to avoid showing preprocessed span markup
  if (role === 'user') {
    return <div className="whitespace-pre-wrap break-words">{content}</div>;
  }

  // For assistant messages, handle markdown rendering
  // Progressive parsing logic:
  // 1. If streaming and no complete tables detected yet, show raw text
  // 2. If streaming but complete tables detected, start parsing progressively
  // 3. If not streaming, always parse markdown
  const shouldShowRaw = isStreaming && !hasBeenParsed && hasIncompleteTable(processedContent);

  if (shouldShowRaw) {
    return (
      <div>
        <div className="whitespace-pre-wrap text-white text-sm sm:text-base">{plainTextContent}</div>
        {tradeRecommendations && !isStreaming && (
          <div className="mt-6 mb-2">
            <p className="text-lg font-semibold text-white dark:text-gray-100 mb-3">{t('trade.interestedIn')}</p>
            <div className="flex flex-wrap gap-3">
              {tradeRecommendations.map((recommendation, index) => (
                <TradeRecommendation
                  key={index}
                  recommendation={recommendation}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // If no tables detected or single text segment, use basic markdown rendering
  if (segments.length === 1 && segments[0].type === 'text') {
    return (
      <div>
        <div className="prose prose-sm max-w-none break-words text-sm text-white sm:text-base">
          <ReactMarkdown
            remarkPlugins={PROSE_REMARK_PLUGINS}
            rehypePlugins={PROSE_REHYPE_PLUGINS}
            components={markdownComponents}>
            {processedContent}
          </ReactMarkdown>
        </div>
        {tradeRecommendations && !isStreaming && (
          <div className="mt-6 mb-2">
            <p className="text-lg font-semibold text-white dark:text-gray-100 mb-3">{t('trade.interestedIn')}</p>
            <div className="flex flex-wrap gap-3">
              {tradeRecommendations.map((recommendation, index) => (
                <TradeRecommendation
                  key={index}
                  recommendation={recommendation}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Render segmented content with special handling for tables
  return (
    <div className="max-w-full text-sm text-white sm:text-base">
      {segments.map((segment, index) => (
        <div key={`${segment.type}-${index}`}>
          {segment.type === 'table' ? (
            (() => {
              const tableData = parseMarkdownTableSafe(segment.content);
              return tableData ? (
                <TableRenderer
                  key={`table-${index}-${segment.content.length}-${tableData.rows.length}`}
                  tableData={tableData}
                />
              ) : (
                // Fallback to raw text if parsing fails
                <div className="my-4 p-4 bg-muted/30 rounded-lg border border-border">
                  <div className="text-xs text-muted-foreground mb-2 font-medium">Table (parsing failed - showing raw markdown)</div>
                  <div className="whitespace-pre-wrap font-mono text-sm text-foreground">{segment.content}</div>
                </div>
              );
            })()
          ) : segment.type === 'chart' ? (
            isKlineLocked ? (
              <LockedKlinePlaceholder
                key={segment.dataId}
                language={language}
                restriction={klineRestriction}
                onUnlockClick={onLockedKlineClick}
              />
            ) : (
              <Chart
                key={segment.dataId}
                type={segment.chartType!}
                dataId={segment.dataId!}
                ticker={segment.ticker}
                studies={segment.studies}
                sessionId={sessionId}
                userId={userId}
              />
            )
          ) : segment.type === 'codeblock' ? (
            (() => {
              // Extract content from code block
              const codeBlockMatch = segment.content.match(/^```(\w*)\s*\n([\s\S]*?)\n```$/);
              if (!codeBlockMatch) {
                // Fallback if regex fails
                return (
                  <div className="prose prose-sm max-w-none">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw, rehypeReference]}>
                      {segment.content}
                    </ReactMarkdown>
                  </div>
                );
              }

              const { content: codeContent } = extractCodeBlockContent(codeBlockMatch);

              // Check if the code content looks like a table
              if (looksLikeTable(codeContent)) {
                // Try to parse as table
                const tableData = parseMarkdownTableSafe(codeContent);
                if (tableData) {
                  return <TableRenderer tableData={tableData} />;
                }
              }

              // Render as regular code block
              return (
                <div className="prose prose-sm max-w-none">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm, remarkReference, remarkChart]}
                    rehypePlugins={[rehypeRaw, rehypeReference, rehypeChart]}
                    components={{
                      code: ({ children, ...props }) => {
                        return (
                          <div className="my-4">
                            <pre className="overflow-x-auto rounded-lg bg-[#231F20] p-3 sm:p-4">
                              <code
                                className="text-sm font-mono"
                                {...props}>
                                {children}
                              </code>
                            </pre>
                          </div>
                        );
                      },
                    }}>
                    {segment.content}
                  </ReactMarkdown>
                </div>
              );
            })()
          ) : (
            <div className="prose prose-sm max-w-none break-words">
              <ReactMarkdown
                remarkPlugins={PROSE_REMARK_PLUGINS}
                rehypePlugins={PROSE_REHYPE_PLUGINS}
                components={markdownComponents}>
                {segment.content}
              </ReactMarkdown>
            </div>
          )}
        </div>
      ))}
      {tradeRecommendations && !isStreaming && (
        <div className="mt-6 mb-2">
          <p className="text-lg font-semibold text-white dark:text-gray-100 mb-3">{t('trade.interestedIn')}</p>
          <div className="flex flex-wrap gap-3">
            {tradeRecommendations.map((recommendation, index) => (
              <TradeRecommendation
                key={index}
                recommendation={recommendation}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
